import React, { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import UPDATE_NONPROFIT_ORG_MISSION from 'Apollo/Mutations/UpdateNonprofitOrgMission'
import { useAppState } from 'Context/AppContext/AppContext'
import { ActionElementType } from 'Components/SharedComponents/Surveys/constants'
import {
  Description,
  StepContainer,
  Title,
} from 'Components/SharedComponents/Surveys/Surveys.styled'
import { NextActions } from 'Components/SharedComponents/Surveys/NextActions/NextActions'
import { UserActions } from 'Components/SharedComponents/Surveys/UserActions/UserActions'
import {
  ButtonAction,
  CustomAction,
} from 'Components/SharedComponents/Surveys/types'
import {
  useGetOrg990AttributesQuery,
  UserSurveyPrimaryFunction,
  UserSurveyRole,
} from 'Generated/graphql-types'
import { analytics } from 'Modules/Analytics'
import { needsAssessment as needsAssesmentAnalytics } from 'Modules/Analytics/trackActionInterface/Account/needsAssessment'
import { HandleAnyAction } from '../../types'
import { NeedsAssessmentWizardStep } from '../../constants'
import { sendStepTransitionEvent } from '../../utils'

type AddYourMissionStatementProps = {
  onAnyAction: HandleAnyAction
  nonprofitOrgId: number
  userJourneyLength: number
  userPrimaryFunction?: UserSurveyPrimaryFunction
  userRole?: UserSurveyRole
}

export const AddYourMissionStatement = ({
  onAnyAction,
  nonprofitOrgId,
  userJourneyLength,
  userPrimaryFunction,
  userRole,
}: AddYourMissionStatementProps) => {
  const {
    userConfig,
    nonprofitOrgConfig: {
      missionStatement: contextMission,
      orgId,
      funderOrgId,
    },
  } = useAppState()

  const {
    data: {
      getOrg990Attributes: { missionDesc: org990Mission = '' } = {},
    } = {},
  } = useGetOrg990AttributesQuery({
    variables: { orgId },
    skip: !orgId || !!contextMission,
  }) as { data: { getOrg990Attributes: { missionDesc: string } } }

  const currentMission = contextMission || org990Mission

  const [updatedMissionStatement, setUpdatedMissionStatement] =
    useState<string>(currentMission || '')

  useEffect(() => {
    if (currentMission) setUpdatedMissionStatement(currentMission)
  }, [currentMission])

  const [updateMissionStatement] = useMutation(UPDATE_NONPROFIT_ORG_MISSION)

  const redirectToDashboard = () => {
    window.location.href = '/impact/dashboard'
  }

  const handleSkipNow = () => {
    redirectToDashboard()
    analytics.trackActionCreator(needsAssesmentAnalytics.Mission_Skipped)({})
  }

  const handleSaveAndCompleteSurvey = async () => {
    try {
      const missionStatement = updatedMissionStatement || currentMission || ''
      // Update the mission statement
      await updateMissionStatement({
        variables: {
          orgInfo: {
            orgId: nonprofitOrgId,
            missionStatement,
          },
        },
      })

      // Log the step transition event
      sendStepTransitionEvent({
        currentStep: NeedsAssessmentWizardStep.ADD_YOUR_MISSION_STATEMENT,
        nextStep: NeedsAssessmentWizardStep.ADD_YOUR_MISSION_STATEMENT,
        userId: userConfig.id,
        nonprofitOrgId: orgId,
        funderOrgId,
        userEmail: userConfig.email,
        userJourneyLength,
        userPrimaryFunction,
        userRole,
      })

      redirectToDashboard()

      analytics.trackActionCreator(needsAssesmentAnalytics.Mission_Added)({
        missionStatement,
      })
    } catch (err) {
      console.error('Error completing survey:', err)
    }
  }

  const missionStatementActions: CustomAction[] = [
    {
      callback: (...args: unknown[]) => {
        const newMissionStatement = args[0] as string
        setUpdatedMissionStatement(newMissionStatement)
      },
      label: 'Mission Statement',
      multiline: true,
      value: updatedMissionStatement || '',
      type: ActionElementType.TEXT_FIELD,
    },
  ]

  const nextActions: ButtonAction[] = [
    {
      label: 'Skip Now',
      saveUserState: true,
      type: ActionElementType.BUTTON,
      callback: handleSkipNow,
    },
    {
      disabled: !updatedMissionStatement,
      label: `${currentMission ? 'Update' : 'Add'} Mission`,
      saveUserState: true,
      type: ActionElementType.BUTTON,
      callback: handleSaveAndCompleteSurvey,
    },
  ]

  return (
    <StepContainer>
      <Title>Add Your Mission Statement</Title>
      <Description>
        It will be visible for all your organization, we will use your mission
        statement when helping you to create more accurate AI stories, summaries
        and reports with Resilia.
      </Description>
      <UserActions
        onAnyAction={onAnyAction}
        userActions={missionStatementActions}
      />
      <NextActions nextActions={nextActions} onAnyAction={onAnyAction} />
    </StepContainer>
  )
}
